"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.EitherAsync = void 0;
const Either_1 = require("./Either");
const MaybeAsync_1 = require("./MaybeAsync");
const helpers = {
    liftEither(either) {
        if (either.isRight()) {
            return Promise.resolve(either.extract());
        }
        throw either.extract();
    },
    fromPromise(promise) {
        return promise.then(helpers.liftEither);
    },
    throwE(error) {
        throw error;
    }
};
class EitherAsyncImpl {
    constructor(runPromise) {
        this.runPromise = runPromise;
        this[_a] = 'EitherAsync';
        this['fantasy-land/map'] = this.map;
        this['fantasy-land/bimap'] = this.bimap;
        this['fantasy-land/chain'] = this.chain;
        this['fantasy-land/ap'] = this.ap;
        this['fantasy-land/extend'] = this.extend;
        this['fantasy-land/alt'] = this.alt;
        this.then = (onfulfilled, onrejected) => {
            return this.run().then(onfulfilled, onrejected);
        };
    }
    leftOrDefault(defaultValue) {
        return this.run().then((x) => x.leftOrDefault(defaultValue));
    }
    orDefault(defaultValue) {
        return this.run().then((x) => x.orDefault(defaultValue));
    }
    join() {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this;
            if (either.isRight()) {
                const nestedEither = await either.extract();
                return helpers.liftEither(nestedEither);
            }
            return helpers.liftEither(either);
        });
    }
    ap(eitherF) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const otherValue = await eitherF;
            if (otherValue.isRight()) {
                const thisValue = await this;
                if (thisValue.isRight()) {
                    return otherValue.extract()(thisValue.extract());
                }
                else {
                    return helpers.liftEither(thisValue);
                }
            }
            return helpers.liftEither(otherValue);
        });
    }
    alt(other) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const thisValue = await this;
            if (thisValue.isRight()) {
                return thisValue.extract();
            }
            else {
                const otherValue = await other;
                return helpers.liftEither(otherValue);
            }
        });
    }
    extend(f) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this.run();
            if (either.isRight()) {
                const v = exports.EitherAsync.liftEither(either);
                return helpers.liftEither((0, Either_1.Right)(f(v)));
            }
            return helpers.liftEither(either);
        });
    }
    async run() {
        try {
            return (0, Either_1.Right)(await this.runPromise(helpers));
        }
        catch (e) {
            return (0, Either_1.Left)(e);
        }
    }
    bimap(f, g) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this.run();
            return helpers.liftEither(either.bimap(f, g));
        });
    }
    map(f) {
        return (0, exports.EitherAsync)((helpers) => this.runPromise(helpers).then(f));
    }
    mapLeft(f) {
        return (0, exports.EitherAsync)(async (helpers) => {
            try {
                return await this.runPromise(helpers);
            }
            catch (e) {
                throw f(e);
            }
        });
    }
    chain(f) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const value = await this.runPromise(helpers);
            return helpers.fromPromise(f(value));
        });
    }
    chainLeft(f) {
        return (0, exports.EitherAsync)(async (helpers) => {
            try {
                return await this.runPromise(helpers);
            }
            catch (e) {
                return helpers.fromPromise(f(e));
            }
        });
    }
    toMaybeAsync() {
        return (0, MaybeAsync_1.MaybeAsync)(async ({ liftMaybe }) => {
            const either = await this.run();
            return liftMaybe(either.toMaybe());
        });
    }
    swap() {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this.run();
            if (either.isRight())
                helpers.throwE(either.extract());
            return helpers.liftEither((0, Either_1.Right)(either.extract()));
        });
    }
    ifLeft(effect) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this.run();
            either.ifLeft(effect);
            return helpers.liftEither(either);
        });
    }
    ifRight(effect) {
        return (0, exports.EitherAsync)(async (helpers) => {
            const either = await this.run();
            either.ifRight(effect);
            return helpers.liftEither(either);
        });
    }
    void() {
        return this.map((_) => { });
    }
    caseOf(patterns) {
        return this.run().then((x) => x.caseOf(patterns));
    }
    finally(effect) {
        return (0, exports.EitherAsync)(({ fromPromise }) => fromPromise(this.run().finally(effect)));
    }
}
_a = Symbol.toStringTag;
exports.EitherAsync = Object.assign((runPromise) => new EitherAsyncImpl(runPromise), {
    fromPromise: (f) => (0, exports.EitherAsync)(({ fromPromise: fP }) => fP(f())),
    liftEither: (either) => (0, exports.EitherAsync)(({ liftEither }) => liftEither(either)),
    lefts: (list) => Promise.all(list.map((x) => x.run())).then(Either_1.Either.lefts),
    rights: (list) => Promise.all(list.map((x) => x.run())).then(Either_1.Either.rights),
    sequence: (eas) => (0, exports.EitherAsync)(async (helpers) => {
        let res = [];
        for await (const e of eas) {
            if (e.isLeft()) {
                return helpers.liftEither(e);
            }
            res.push(e.extract());
        }
        return helpers.liftEither((0, Either_1.Right)(res));
    }),
    all: (eas) => exports.EitherAsync.fromPromise(async () => Promise.all(eas).then(Either_1.Either.sequence))
});
EitherAsyncImpl.prototype.constructor = exports.EitherAsync;
